import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Container, PageHero } from "@/components/ui-blocks";
import { COMPANY } from "@/data/mockData";
import Seo from "@/components/Seo";
import { submitContact } from "@/services/api";
import { Mail, Phone, MessageCircle, Instagram, Facebook, Youtube, Music2 } from "lucide-react";
import { toast } from "sonner";

export default function Contact() {
  // Bulk-order and overseas-delivery enquiries arrive here from the book page
  // and checkout, with the subject already written so the buyer only has to add
  // their details.
  const [params] = useSearchParams();
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    subject: params.get("subject") || "",
    message: params.get("message") || "",
  });
  const [submitting, setSubmitting] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in name, email and message");
      return;
    }
    setSubmitting(true);
    await submitContact(form);
    setSubmitting(false);
    toast.success("Thank you", { description: "We'll be in touch shortly." });
    setForm({ name: "", email: "", phone: "", subject: "", message: "" });
  };

  return (
    <div data-testid="contact-page">
      <Seo title="Contact" description="Get in touch with Ace Asia Productions — enquiries about our books, collectibles, multimedia, and partnerships." />
      <PageHero
        testId="contact-hero"
        overline="Contact Us"
        title="Let's begin a conversation."
        subtitle="Editorial enquiries, bulk orders, collaboration proposals, or simply a hello — we'd love to hear from you."
      />

      <section className="bg-[#FDFBF7] py-20">
        <Container>
          <div className="grid lg:grid-cols-12 gap-12">
            <form
              onSubmit={handleSubmit}
              data-testid="contact-form"
              className="lg:col-span-7 space-y-6"
            >
              <div className="grid sm:grid-cols-2 gap-6">
                <Field label="Name *" testId="contact-name" value={form.name} onChange={(v) => update("name", v)} />
                <Field label="Email *" type="email" testId="contact-email" value={form.email} onChange={(v) => update("email", v)} />
              </div>
              <div className="grid sm:grid-cols-2 gap-6">
                <Field label="Phone" testId="contact-phone" value={form.phone} onChange={(v) => update("phone", v)} />
                <Field label="Subject" testId="contact-subject" value={form.subject} onChange={(v) => update("subject", v)} />
              </div>
              <label className="block">
                <span className="text-xs uppercase tracking-[0.15em] text-[#4A3728]">Message *</span>
                <textarea
                  data-testid="contact-message"
                  value={form.message}
                  onChange={(e) => update("message", e.target.value)}
                  rows={6}
                  className="w-full bg-transparent border-b border-[#6e5022]/40 focus:border-[#6e5022] py-2.5 outline-none text-sm resize-none"
                />
              </label>
              <button
                data-testid="contact-submit"
                type="submit"
                disabled={submitting}
                className="bg-[#6e5022] hover:bg-[#946f36] text-[#FDFBF7] px-8 py-4 text-xs uppercase tracking-[0.2em] transition-colors disabled:opacity-60"
              >
                {submitting ? "Sending…" : "Send Message"}
              </button>
            </form>

            <aside className="lg:col-span-5 space-y-8">
              <div className="bg-[#281a12] text-[#FDFBF7] p-8">
                <div className="overline text-[#946f36] mb-6">Reach Us Directly</div>
                <Item icon={Mail} label="Email" value={COMPANY.email} href={`mailto:${COMPANY.email}`} testId="contact-direct-email" />
                <Item icon={Phone} label="Mobile" value={COMPANY.phone} href={`tel:${COMPANY.phone.replace(/\s+/g, "")}`} testId="contact-direct-phone" />
                <a
                  href={COMPANY.whatsapp}
                  target="_blank"
                  rel="noreferrer noopener"
                  data-testid="contact-whatsapp-cta"
                  className="mt-6 inline-flex items-center gap-2 bg-[#946f36] hover:bg-[#FDFBF7] hover:text-[#281a12] text-[#281a12] px-7 py-3.5 text-xs uppercase tracking-[0.2em] transition-colors"
                >
                  <MessageCircle className="w-4 h-4" strokeWidth={1.5} /> WhatsApp Us
                </a>
                <div
                  className="mt-5 text-xs text-[#D1C3B4]"
                  data-testid="contact-preferred-mode"
                >
                  Preferred mode of communication:{" "}
                  <span className="text-[#FDFBF7]">
                    WhatsApp only — +65 9199 4901
                  </span>
                </div>
              </div>

              <div className="border border-[#6e5022]/25 p-8">
                <div className="overline text-[#946f36] mb-4">Follow</div>
                <div className="flex gap-4 flex-wrap">
                  <Social href={COMPANY.socials.instagram} icon={Instagram} testId="contact-social-instagram" />
                  <Social href={COMPANY.socials.facebook} icon={Facebook} testId="contact-social-facebook" />
                  <Social href={COMPANY.socials.youtube} icon={Youtube} testId="contact-social-youtube" />
                  <Social href={COMPANY.socials.tiktok} icon={Music2} testId="contact-social-tiktok" />
                </div>
                <div className="mt-6 pt-6 border-t border-[#6e5022]/20 text-xs text-[#4A3728]">
                  {COMPANY.name}<br />
                  UEN: {COMPANY.uen}
                </div>
              </div>
            </aside>
          </div>
        </Container>
      </section>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testId }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-[0.15em] text-[#4A3728]">{label}</span>
      <input
        data-testid={testId}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-transparent border-b border-[#6e5022]/40 focus:border-[#6e5022] py-2.5 outline-none text-sm"
      />
    </label>
  );
}

function Item({ icon: Icon, label, value, href, testId }) {
  return (
    <a href={href} data-testid={testId} className="flex items-start gap-4 py-3 border-b border-[#FDFBF7]/10 group">
      <Icon className="w-5 h-5 text-[#946f36] mt-0.5" strokeWidth={1.5} />
      <div>
        <div className="overline text-[#946f36]">{label}</div>
        <div className="text-[#FDFBF7] group-hover:text-[#946f36]">{value}</div>
      </div>
    </a>
  );
}

function Social({ href, icon: Icon, testId }) {
  return (
    <a href={href} target="_blank" rel="noreferrer noopener" data-testid={testId} className="w-12 h-12 border border-[#6e5022]/30 flex items-center justify-center hover:bg-[#6e5022] hover:text-[#FDFBF7] transition-colors text-[#6e5022]">
      <Icon className="w-5 h-5" strokeWidth={1.5} />
    </a>
  );
}
